import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBackButton } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';

interface AppShellProps {
  children: ReactNode;
  className?: string;
}

/**
 * Top-level layout wrapper.
 *
 * Responsibilities:
 *   1. Apply safe-area padding (Telegram devices with notches).
 *   2. Keep the overall container scrollable while the header sticks.
 *   3. Wire the Telegram BackButton to React Router's navigate(-1) for
 *      every route except the index ('/'), so users always have a clear
 *      escape hatch.
 */
export function AppShell({ children, className }: AppShellProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const canGoBack = location.pathname !== '/';

  useBackButton(canGoBack ? () => navigate(-1) : null);

  useEffect(() => {
    // Scroll to top on route change.
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div
      className={cn(
        'mx-auto flex min-h-full w-full max-w-xl flex-col gap-6 px-4 pb-24 pt-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
